<template>
  <BaseFormLayout
    :title="
      editMode
        ? `Edit ${configurationModule.warehouse}`
        : `Add ${configurationModule.warehouse}`
    "
    :nonFieldErrors="formErrors.non_field_errors"
    :isHeader="isHeader"
  >
    <template #actions>
      <SecondaryButton
        id="warehouseReset"
        color="secondary"
        depressed
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
        Reset
      </SecondaryButton>
      <PrimaryButton
        id="warehouseSubmit"
        btnClass="text-uppercase rounded-lg"
        @click="submitForm()"
        :disabled="!isValid"
        name="Submit"
      >
      </PrimaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="warehouseForm"
        class="h-100"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-row no-gutters class="h-100 ma-0">
          <v-col cols="12" lg="4">
            <v-card
              elevation="0"
              class="pa-4 h-100 mr-lg-4 rounded-lg overflow-y-auto"
              style="height: calc(100vh - 253px)"
            >
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold"
                    >{{ configurationModule.branch }} Information</span
                  >
                </v-col>
                <v-col cols="12">
                  <SelectField
                    id="branch"
                    v-model="warehouseDetails.branch"
                    :label="`${configurationModule.branch}*`"
                    name="branch"
                    item-text="name"
                    item-value="id"
                    :disabled="isBranchDisable"
                    :items-list="branchList"
                    :rules="[
                      (val) =>
                        !!val || `${configurationModule.branch} is required`,
                    ]"
                    :error-messages="formErrors.branch"
                    @input="
                      delete formErrors.branch;
                      setAutoCompletedRestriction();
                    "
                  />
                </v-col>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold"
                    >{{ configurationModule.warehouse }} Information</span
                  >
                </v-col>
                <v-col cols="12">
                  <InputField
                    id="name"
                    v-model="warehouseDetails.name"
                    :label="`${configurationModule.warehouse} Name`"
                    :required="true"
                    name="name"
                    :error-messages="formErrors.name"
                    @input="delete formErrors.name"
                  />
                </v-col>
                <v-col cols="12">
                  <InputField
                    id="warehouse_id"
                    v-model="warehouseDetails.warehouse_id"
                    :label="`${configurationModule.warehouse} Code`"
                    name="warehouse_id"
                    required
                    :rules="[
                      (val) =>
                        (val && val.length >= 2 && val.length <= 10) ||
                        `${configurationModule.warehouse} Code should be between 2 to 10 Characters!`,
                    ]"
                    :error-messages="formErrors.warehouse_id"
                    @input="delete formErrors.warehouse_id"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    ref="addressField"
                    id="addressField"
                    hide-details="auto"
                    name="address"
                    label="Address*"
                    dense
                    outlined
                    clearable
                    :class="
                      !warehouseDetails.branch
                        ? 'unassigned lighten-3'
                        : 'background_color'
                    "
                    :disabled="!warehouseDetails.branch ? true : false"
                    v-model="warehouseDetails.address"
                    :rules="[(val) => !!val || 'Address is required']"
                    :error-messages="formErrors.address"
                    @input="
                      delete formErrors.address;
                      delete formErrors.coordinates;
                    "
                    @click:clear="setRestLocation"
                  />
                </v-col>
                <v-col
                  cols="6"
                  v-if="warehouseDetails && warehouseDetails.coordinates"
                >
                  <InputField
                    id="latitude"
                    disabled
                    class="unassigned lighten-3"
                    name="latitude"
                    v-model="warehouseDetails.coordinates.latitude"
                    label="Latitude"
                    :error-messages="formErrors.coordinates"
                    @input="delete formErrors.coordinates"
                    @change="delete formErrors.coordinates"
                  />
                </v-col>
                <v-col
                  cols="6"
                  v-if="warehouseDetails && warehouseDetails.coordinates"
                >
                  <InputField
                    id="longitude"
                    disabled
                    class="unassigned lighten-3"
                    name="longitude"
                    v-model="warehouseDetails.coordinates.longitude"
                    label="Longitude"
                    :error-messages="formErrors.coordinates"
                    @input="delete formErrors.coordinates"
                    @change="delete formErrors.coordinates"
                  />
                </v-col>
                <v-col cols="12">
                  <InputField
                    type="number"
                    id="loading_time"
                    v-model="warehouseDetails.loading_time"
                    label="Loading Time (Minutes)"
                    name="loading_time"
                    :required="true"
                    :rules="[
                      (val) => val > 0 || 'Loading time can not be less then 0',
                    ]"
                    :error-messages="formErrors.loading_time"
                    @input="delete formErrors.loading_time"
                  />
                </v-col>
                <v-col cols="12">
                  <InputField
                    type="number"
                    id="offloading_time"
                    v-model="warehouseDetails.offloading_time"
                    label="Off Loading Time (Minutes)"
                    name="offloading_time"
                    :required="true"
                    :rules="[
                      (val) =>
                        val > 0 || 'Off loading time can not be less then 0',
                    ]"
                    :error-messages="formErrors.offloading_time"
                    @input="delete formErrors.offloading_time"
                  />
                </v-col>

                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold">
                    Operating Time
                  </span>
                </v-col>
                <v-col cols="12">
                  <InputField
                    id="from_time"
                    type="time"
                    v-model="warehouseDetails.from_time"
                    label="From Time"
                    name="from_time"
                    :required="true"
                    :error-messages="formErrors.from_time"
                    @input="delete formErrors.from_time"
                  />
                </v-col>
                <v-col cols="12">
                  <InputField
                    id="to_time"
                    type="time"
                    v-model="warehouseDetails.to_time"
                    label="To Time"
                    name="to_time"
                    :required="true"
                    :error-messages="formErrors.to_time"
                    @input="delete formErrors.to_time"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="8" class="mt-4 mt-lg-0 rounded-lg">
            <GoogleMap
              mapId="warehouseMap"
              mapWidth="100%"
              mapHeight="calc(100vh - 160px)"
              ref="warehouseMap"
              :showLocationName="true"
              :isDrawable="false"
              :hasMapInfo="true"
            >
              <template #mapInfo>
                <MapInfo legendType="warehouse" />
              </template>
            </GoogleMap>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </BaseFormLayout>
</template>

<script>
import { bus } from "@/main.js";
import { loadGoogleInstance, warehouseIdValidation } from "@/utils/functions";

import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import GoogleMap from "@/components/BaseComponents/GoogleMap.vue";
import { configurationModule } from "@/utils/moduleMapping";
import MapInfo from "@/components/BaseComponents/MapInfo.vue";

export default {
  components: {
    GoogleMap,
    BaseFormLayout,
    MapInfo,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      required: true,
    },
    isHeader: {
      type: Boolean,
      default: true,
    },
    isBranchDisable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      warehouseIdValidation,
      configurationModule,
      fileInput: null,
      formCount: 0,
      isValid: true,
      warehouseDetails: {},
      branchDetails: {},
      formErrors: {},
      branchList: [],
      userDetails: this.$helper.getUserDetailsLocalStorage(),
    };
  },
  computed: {},
  methods: {
    resetForm() {
      this.$refs.warehouseForm.reset();
      let inter = setInterval(() => {
        if (this.$refs.warehouseMap) {
          clearInterval(inter);
          this.$refs.warehouseMap.clearGeoJson();
        }
      });
      if ("companyId" in this.$route.query) {
        this.warehouseDetails.company = this.$route.query.companyId;
      }
    },
    getBranchList() {
      let payload = {
        limit: "all",
      };
      if ("companyId" in this.$route.query) {
        payload.company = this.$route.query.companyId;
      }

      bus.$emit("showLoader", true);
      this.$api.branch
        .getBranchSelectList(payload)
        .then((res) => {
          this.branchList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          bus.$emit("showLoader", false);
        });
    },
    loadEditablePolygon() {
      let inter = setInterval(() => {
        if (
          this.$refs.warehouseMap &&
          window &&
          window.google &&
          window.google.maps
        ) {
          clearInterval(inter);

          this.$refs.warehouseMap.clearGeoJson();
          if (this.branchDetails && this.branchDetails.serviceable_area) {
            this.$refs.warehouseMap.addFeatures([
              {
                geometry: this.branchDetails.serviceable_area,
                type: "Feature",
              },
            ]);
          }
        }
      });
    },
    getBranchDetail(id) {
      this.$api.branch
        .getBranchDetail(id)
        .then((res) => {
          this.branchDetails = res.data;

          let inter = setInterval(() => {
            if (
              this.$refs.warehouseMap &&
              window &&
              window.google &&
              window.google.maps
            ) {
              clearInterval(inter);

              if (
                this.addressField &&
                this.branchDetails &&
                this.branchDetails.country_code
              )
                this.addressField.setComponentRestrictions({
                  country: [this.branchDetails.country_code],
                });

              this.loadEditablePolygon();
              // this.$refs.warehouseMap.setCustomMaker(
              //   {
              //     ...this.branchDetails,
              //     icon: "Branch",
              //     title: "Branch Details",
              //   },
              //   ["country"]
              // );
            }
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getWarehouseObject(id) {
      this.$api.warehouse
        .getWarehouseObject(id)
        .then((res) => {
          this.warehouseDetails = res.data;
          this.getBranchDetail(this.warehouseDetails.branch);

          let inter = setInterval(() => {
            if (
              this.$refs.warehouseMap &&
              window &&
              window.google &&
              window.google.maps
            ) {
              clearInterval(inter);

              this.$refs.warehouseMap.removeAllMarkers();
              setTimeout(() => {
                this.$refs.warehouseMap.setCustomMaker(
                  {
                    ...this.warehouseDetails,
                    icon: "warehouse",
                    title: "Warehouse Details",
                  },
                  ["branch"]
                );
              }, 300);
            }
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
    submitForm() {
      if (!this.$refs.warehouseForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }
      let data = { ...this.warehouseDetails };

      if (this.editMode) {
        this.$api.warehouse
          .updateWarehouseObject({
            id: this.warehouseDetails.id,
            data: data,
          })
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Warehouse information updated!",
              color: "success",
            });
            this.resetForm();
            this.$router.push({ name: "warehouse-list" });
          })
          .catch((err) => {
            if (err.data) {
              this.formErrors = err.data;
            }
          });
      } else {
        this.$api.warehouse
          .createWarehouseObject(data)
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Warehouse created!",
              color: "success",
            });
            this.resetForm();
            if (!this.isHeader) {
              this.$emit("redirect", true);
            } else {
              this.$router.push({ name: "warehouse-list" });
            }
          })
          .catch((err) => {
            if (err.data) {
              this.formErrors = err.data;
            }
          });
      }
    },
    setAutoCompletedRestriction() {
      if (this.warehouseDetails.branch && this.branchList) {
        let branch = this.branchList.find(
          (v) => v.id == this.warehouseDetails.branch
        );
        this.getBranchDetail(this.warehouseDetails.branch);
        if (branch && branch.country_code) {
          this.addressField.setComponentRestrictions({
            country: [branch.country_code],
          });
        }
      }
      this.setRestLocation();
    },
    setRestLocation() {
      this.warehouseDetails.address = null;
      this.warehouseDetails.coordinates = {};
      this.$refs.warehouseForm.resetValidation();
    },
    loadAutoComplete() {
      const interval = setInterval(async () => {
        if (this.$refs.addressField && window.google && window.google.maps) {
          clearInterval(interval);

          let addressField = document.getElementById("addressField");

          this.addressField = new window.google.maps.places.Autocomplete(
            addressField
          );

          if (this.userDetails?.preferred_branch?.country_code) {
            this.addressField.setComponentRestrictions({
              country: [this.userDetails.preferred_branch.country_code],
            });
          } else {
            this.addressField.setComponentRestrictions({
              country: ["AE"],
            });
          }

          addressField.setAttribute("placeholder", "");

          this.addressField.addListener("place_changed", () => {
            let place = this.addressField.getPlace();

            this.warehouseDetails = {
              ...this.warehouseDetails,
              coordinates: {
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
              },
              address: place.formatted_address,
            };
            this.$refs.warehouseMap.removeAllMarkers();
            this.$refs.warehouseMap.setCustomMaker(
              {
                ...this.warehouseDetails,
                icon: "warehouse",
                title: "Warehouse Details",
              },
              ["branch"]
            );
            // this.$refs.warehouseMap.setCustomMaker({
            //   ...this.branchDetails,
            //   icon: "Branch",
            //   title: "Branch Details",
            // });
          });
        } else {
          loadGoogleInstance();
        }
      }, 100);
    },
  },
  mounted() {
    this.loadAutoComplete();
    this.getBranchList();
    if ("companyId" in this.$route.query) {
      this.warehouseDetails.company = this.$route.query.companyId;
    }
    if (this.$route && this.$route.params && this.$route.params.id) {
      this.getWarehouseObject(this.$route.params.id);
    }
    let inter = setInterval(() => {
      let obj = this.$helper.getUserDetailsLocalStorage();
      if (!this.editMode && obj.preferred_branch && obj.preferred_branch.id) {
        clearInterval(inter);
        this.warehouseDetails = {
          branch: obj.preferred_branch.id,
        };
      }
    }, 200);
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
</style>
